import React from 'react';
import { Controller, Control, UseFormWatch, FieldErrors } from 'react-hook-form';
import { TextField, Tooltip } from '@material-ui/core';
import moment, { Moment } from 'moment';

import { controlledDatePicker } from 'components/ReactHookForm/controlledDatePicker';

import { ChallengeFormData } from '../types';
import { labelStyle } from '../utils';

const StartDateInput = controlledDatePicker<ChallengeFormData>()('startDate');
const EndDateInput = controlledDatePicker<ChallengeFormData>()('endDate');

interface SettingsStepProps {
  control: Control<ChallengeFormData>;
  watch: UseFormWatch<ChallengeFormData>;
  errors: FieldErrors<ChallengeFormData>;
}

function SettingsStep({ control, watch, }): SettingsStepProps {
  const isEditing = watch('id');
  const challengeStatus = watch('status');
  const savedStartDate = watch('startDate');
  const today = moment().startOf('day');

  const savedDateOrDefault = isEditing && savedStartDate ? savedStartDate : today;
  const minStartingDate = moment.min(savedDateOrDefault, today);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <p>Challenge Start &amp; End Date *</p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <StartDateInput
            controllerProps={{
              control,
              rules: { required: 'Start date is required for published challenges' },
            }}
            label="Starting At"
            inputFormat="yyyy-MM-DD"
            minDate={minStartingDate}
            disabled={isEditing && challengeStatus === 'published'}
          />
          <EndDateInput
            controllerProps={{
              control,
              rules: {
                required: 'End date is required for published challenges',
                validate: {
                  minDate: (date: Moment | string | null) => {
                    const endDate = typeof date === 'string' ? moment.utc(date) : date;
                    const startDate =
                      watch('startDate') && moment.utc(watch('startDate') as string);

                    if (endDate && startDate && endDate.isBefore(startDate.startOf('day'), 'day')) {
                      return 'End date should be after the start date';
                    }
                    return true;
                  },
                },
              },
            }}
            label="Ending At"
            inputFormat="yyyy-MM-DD"
            minDate={watch('startDate') ? moment.utc(watch('startDate') as string) : undefined}
          />
          <Tooltip title="Challenge dates will be displayed in the user's local timezone">
            <div style={{ marginLeft: 10, fontSize: 11, color: 'rgba(0, 0, 0, 0.54)' }}>
              UTC{moment().format('Z')}
            </div>
          </Tooltip>
        </div>
      </div>
      <div style={{ marginBottom: 20, marginTop: 20 }}>
        <p style={{ marginBottom: 5 }}>Position *</p>
        <p style={{ ...labelStyle, marginBottom: 20 }}>
          Select the position in which this challenge will be shown on the homepage along with the
          other challenges. EX: If you have 3 challenges published and you select 1, then this
          challenge will be shown first on the homepage
        </p>
        <Controller
          name="sorting"
          control={control}
          render={({ field: { onChange, ...field } }) => (
            <TextField
              {...field}
              onChange={(e) => {
                const value = e.target.value;
                // Convert to number or undefined if empty
                onChange(value === '' ? undefined : parseInt(value, 10));
              }}
              label="Position"
              type="number"
              fullWidth
              variant="outlined"
              inputProps={{ min: 0, max: 9999 }}
              helperText="Order in which challenge appears (lower numbers first)"
            />
          )}
        />
      </div>
    </div>
  );
}

export default SettingsStep;
